import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import ReturnDispatchCard from './ReturnDispatchCard';

/* ═══════════════════════════════════════════════════════════════════════════
   DISPUTE HELPERS (Add after imports)
═══════════════════════════════════════════════════════════════════════════ */

// Helper: Determine if order is in dispute flow
const isDisputeOrder = (currentStage) => {
  return [
    "Dispute_Raised",
    "Dispute_Under_Investigation",
    "Full_Order_Returned",
    "Partial_Order_Returned",
  ].includes(currentStage);
};

// Helper: Get return type from current stage
const getReturnTypeFromStage = (stage) => {
  if (stage === "Full_Order_Returned") return "Full";
  if (stage === "Partial_Order_Returned") return "Partial";
  return null;
};

const DISPUTE_STAGES = [
  { key: "Dispute_Raised", label: "Dispute Raised", icon: "⚠️" },
  { key: "Dispute_Under_Investigation", label: "Under Investigation", icon: "🔍" },
  { key: "Resolved", label: "Resolved", icon: "✅" },
];
// Dispute Stage Timeline Component
const DisputeStageTimeline = ({ currentStage }) => {
  const activeIdx = DISPUTE_STAGES.findIndex((s) => s.key === currentStage);
  const isResolved =
    currentStage === "Full_Order_Returned" ||
    currentStage === "Partial_Order_Returned" ||
    (currentStage === "Completed" && isDisputeOrder(currentStage));

  const displayStages = DISPUTE_STAGES.map((stage, idx) => {
    if (idx === 2 && isResolved) {
      const returnType = getReturnTypeFromStage(currentStage);
      if (returnType) {
        return {
          key: currentStage,
          label: `${returnType} Order Returned`,
          icon: "📦",
        };
      }
    }
    return stage;
  });

  return (
    <div className="space-y-0">
      {displayStages.map((stage, idx) => {
        const isDone = idx < activeIdx;
        const isCurrent = idx === activeIdx;
        const isLast = idx === displayStages.length - 1;

        return (
          <div key={stage.key} className="flex items-start gap-3">
            <div className="flex flex-col items-center">
              <div className="relative">
                {/* ─── ADD THIS PING ANIMATION ─── */}
                {isCurrent && (
                  <span className="absolute inset-0 rounded-full animate-ping bg-amber-500/20 scale-150" />
                )}

                <div
                  className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center border-2 z-10 transition-all duration-500
                    ${isDone
                      ? "bg-amber-500 border-amber-500 shadow-md shadow-amber-300/30"
                      : isCurrent
                        ? "bg-amber-500 border-amber-500 text-white shadow-lg shadow-amber-300/40 scale-110"
                        : "bg-white border-gray-200"
                    }`}
                >
                  {isDone ? (
                    <svg
                      className="w-3.5 h-3.5 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  ) : (
                    <span className={`text-sm ${isCurrent ? "text-white animate-bounce" : ""}`}>
                      {stage.icon}
                    </span>
                  )}
                </div>
              </div>
              {!isLast && (
                <div
                  className={`w-0.5 flex-1 min-h-[32px] my-0.5 rounded-full transition-all duration-700
  ${isDone
                      ? "bg-gradient-to-b from-amber-500 to-amber-300"
                      : isCurrent
                        ? "bg-gradient-to-b from-amber-500 to-gray-200"
                        : "bg-gray-200"
                    }`}
                />
              )}
            </div>
            <div className="pb-4 pt-1.5">
              <p
                className={`text-sm font-semibold transition-all duration-300
  ${isCurrent
                    ? "text-amber-600 scale-105"
                    : isDone
                      ? "text-gray-700"
                      : "text-gray-400"
                  }`}
              >
                {stage.label}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

/* ─── Dispute Alert Banner (User Side) ─── */
const DisputeAlertBanner = ({ currentStage }) => {
  const isInvestigation = currentStage === "Dispute_Under_Investigation";
  const isResolved =
    currentStage === "Full_Order_Returned" ||
    currentStage === "Partial_Order_Returned" ||
    (currentStage === "Completed" && isDisputeOrder(currentStage));

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        @keyframes warningPulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
      `}</style>

      <div className="relative overflow-hidden rounded-2xl border border-amber-300 bg-white p-5">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "repeating-linear-gradient(45deg,transparent,transparent 10px,currentColor 10px,currentColor 11px)",
            color: "#f59e0b",
          }}
        />

        <div className="relative flex items-center justify-between mb-5">
          <p className="text-[10px] font-bold uppercase tracking-widest text-amber-500 flex items-center gap-2">
            <span className="inline-block w-3 h-0.5 bg-amber-500 rounded-full" />
            Dispute Status
            <span className="inline-block w-3 h-0.5 bg-amber-500 rounded-full" />
          </p>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-100 border border-amber-300">
            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[12px] font-bold text-amber-600">
              {isInvestigation
                ? "Under Investigation"
                : isResolved
                  ? "Resolved"
                  : "Dispute Raised"}
            </span>
          </div>
        </div>

        <div className="relative flex items-center justify-center gap-4 py-2">
          <div className="relative flex-shrink-0">
            <div className="absolute w-16 h-16 inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-16 h-16 rounded-full bg-amber-400/30 animate-ping" />
            </div>
            <div
              className="relative z-10 w-14 h-14 rounded-full flex items-center justify-center text-3xl border-3
              bg-amber-50 border-amber-400 shadow-lg shadow-amber-300/50"
              style={{ animation: "warningPulse 2s ease-in-out infinite" }}
            >
              ⚠️
            </div>
          </div>

          <div className="flex-1">
            <h3 className="text-sm font-bold text-amber-600 mb-1">
              {isInvestigation
                ? "Dispute Under Investigation"
                : isResolved
                  ? "Dispute Resolved"
                  : "You Have an Active Dispute"}
            </h3>

            <p className="text-xs text-amber-700 leading-relaxed">
              {isInvestigation
                ? "Our Order Fulfillment Manager (OFM) is reviewing your dispute. You'll be notified once the investigation is complete."
                : isResolved
                  ? `Dispute has been resolved. Order marked as ${getReturnTypeFromStage(currentStage) ? "returned" : "completed"
                  }.`
                  : "A dispute has been raised on this order. Our team is reviewing it. You will be notified once a resolution is reached."}
            </p>
          </div>

          <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
            <div
              className="absolute inset-y-0 left-0 w-1/3 bg-gradient-to-r from-transparent via-amber-200/20 to-transparent"
              style={{ animation: "shimmer 3s infinite" }}
            />
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-amber-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
            <span className="text-[11px] text-amber-600 font-semibold">
              {isInvestigation
                ? "Verification in Progress"
                : isResolved
                  ? "Ready for Completion"
                  : "Awaiting Resolution"}
            </span>
          </div>

          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-100 border border-amber-200">
            <span className="text-[10px] text-amber-600">
              {isInvestigation
                ? "🔍 OFM Reviewing"
                : isResolved
                  ? "✓ Complete"
                  : "⏱️ Under Review"}
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export const getDeliveryDays = (from, to) => {
  if (!from || !to) return null;

  const diff = new Date(to) - new Date(from);

  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

/* ─── Image / Video Modal ──────────────────────────────── */
export const ImageModal = ({ images, videos, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const allMedia = [...(images || []), ...(videos || [])];

  useEffect(() => {
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const goToNext = () => setCurrentIndex((prev) => (prev + 1) % allMedia.length);
  const goToPrev = () => setCurrentIndex((prev) => (prev - 1 + allMedia.length) % allMedia.length);
  const isVideo = (url) => url?.includes('.mp4') || url?.includes('.webm') || url?.includes('video');

  return createPortal(
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          {isVideo(allMedia[currentIndex]) ? (
            <video src={allMedia[currentIndex]} controls autoPlay className="w-full max-h-[75vh] object-contain bg-black" />
          ) : (
            <img src={allMedia[currentIndex]} alt="Media" className="w-full max-h-[75vh] object-contain bg-black" />
          )}
        </div>
        {allMedia.length > 1 && (
          <>
            <button onClick={goToPrev} className="absolute left-3 top-1/2 -translate-y-1/2 bg-primaryBtnColor hover:bg-primaryBtnHoverColor backdrop-blur-sm text-white rounded-full p-3 border border-white/20 transition-all">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
            </button>
            <button onClick={goToNext} className="absolute right-3 top-1/2 -translate-y-1/2 bg-primaryBtnColor hover:bg-primaryBtnHoverColor backdrop-blur-sm text-white rounded-full p-3 border border-white/20 transition-all">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
            </button>
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-black/70 backdrop-blur-sm text-white px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest border border-white/10">
              {currentIndex + 1} / {allMedia.length}
            </div>
          </>
        )}
      </div>
    </div>, document.body
  );
};

/* ─── Animated Stage Timeline ──────────────────────────── */
const StageTimeline = ({ stages, currentStageIndex, orderStages }) => {
  const [animated, setAnimated] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setAnimated(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const isClosed = orderStages === 'Closed';

  return (
    <div ref={ref} className="w-full">
      {/* Desktop Timeline */}
      <div className="hidden sm:block">
        <div className="relative flex items-start justify-between">
          <div className="absolute top-5 left-[calc(100%/12)] right-[calc(100%/12)] h-0.5 bg-gray-200" />
          <div
            className="absolute top-5 h-0.5 bg-gradient-to-r from-[#772037] to-[#CC234F] transition-all ease-out"
            style={{
              left: `calc(100% / ${stages.length * 2})`,
              width: animated && !isClosed
                ? `${(currentStageIndex / (stages.length - 1)) * (100 - 100 / stages.length)}%`
                : '0%',
              transitionDuration: '1.2s',
              transitionDelay: '0.3s',
            }}
          />
          {stages.map((stage, index) => {
            const isCompleted = index < currentStageIndex;
            const isCurrent = index === currentStageIndex;
            return (
              <div key={stage.key} className="flex flex-col items-center relative z-10 flex-1 gap-3">
                <div className="relative">
                  {isCurrent && !isClosed && (
                    <span className="absolute inset-0 rounded-full animate-ping bg-[#772037]/20 scale-150" />
                  )}
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 relative z-10
                      ${isCompleted
                        ? 'bg-[#772037] border-[#772037] shadow-lg shadow-[#772037]/30'
                        : isCurrent && !isClosed
                          ? 'bg-[#772037] border-[#772037] text-white shadow-lg shadow-[#772037]/40 scale-110'
                          : isClosed && index === 1 ? 'bg-red-500 border-red-500'
                            : 'bg-white border-gray-200'}`}
                    style={{
                      transform: animated ? 'scale(1)' : 'scale(0.5)',
                      opacity: animated ? 1 : 0,
                      transition: `all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${index * 120 + 200}ms`,
                    }}
                  >
                    {isCompleted ? (
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span
                        className={`text-base leading-none ${isCurrent && !isClosed ? 'animate-bounce text-white' : ''
                          }`}
                      >
                        {stage.icon}
                      </span>
                    )}
                  </div>
                </div>
                <p className={`text-[11px] font-semibold text-center leading-tight transition-colors duration-300 px-1
                  ${isCurrent && !isClosed ? 'text-[#772037]' : isCompleted ? 'text-gray-700' : 'text-gray-400'}`}>
                  {stage.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile Timeline — vertical */}
      <div className="sm:hidden space-y-0">
        {stages.map((stage, index) => {
          const isCompleted = index < currentStageIndex;
          const isCurrent = index === currentStageIndex;
          const isLast = index === stages.length - 1;
          return (
            <div key={stage.key} className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div className="relative">
                  {isCurrent && !isClosed && (
                    <span className="absolute inset-0 rounded-full animate-ping bg-[#772037]/20 scale-150" />
                  )}

                  <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center border-2 z-10 transition-all duration-500
                    ${isCompleted
                      ? 'bg-[#772037] border-[#772037] shadow-md shadow-[#772037]/30'
                      : isCurrent && !isClosed
                        ? 'bg-[#772037] border-[#772037] text-white shadow-lg shadow-[#772037]/40 scale-110'
                        : isClosed && index === 1 ? 'bg-red-500 border-red-500'
                          : 'bg-white border-gray-200'}`}>
                    {isCompleted ? (
                      <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <span className={`text-sm ${isCurrent && !isClosed ? 'text-white animate-bounce' : ''}`}>
                        {stage.icon}
                      </span>
                    )}
                  </div>
                </div>
                {!isLast && (
                  <div
                    className={`w-0.5 flex-1 min-h-[32px] my-0.5 rounded-full transition-all duration-700
  ${isCompleted
                        ? 'bg-gradient-to-b from-[#772037] to-[#CC234F]'
                        : isCurrent
                          ? 'bg-gradient-to-b from-[#772037] to-gray-200'
                          : 'bg-gray-200'}`}
                  />
                )}
              </div>
              <div className="pb-4 pt-1.5">
                <p
                  className={`text-sm font-semibold transition-all duration-300
  ${isCurrent && !isClosed
                      ? 'text-[#772037] scale-105'
                      : isCompleted
                        ? 'text-gray-700'
                        : 'text-gray-400'}`}
                >
                  {stage.label}
                </p>

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─── Category → SubCategory → Product → Variety Hierarchy ── */
const ProductHierarchy = ({ order }) => {
  const steps = [
    // {
    //   label: 'Category',
    //   name: order.category?.name,
    //   img: order.category?.img,
    //   hsCode: order.category?.hsCode,
    //   badge: null,
    // },
    // {
    //   label: 'Sub-Category',
    //   name: order.subCategory?.name,
    //   img: order.subCategory?.img,
    //   hsCode: order.subCategory?.hsCode,
    //   badge: null,
    // },
    // {
    //   label: 'Product',
    //   name: order.product?.name,
    //   img: order.product?.img,
    //   hsCode: order.product?.hsCode,
    //   badge: order.product?.botanicalName && order.product.botanicalName !== order.product.name
    //     ? order.product.botanicalName
    //     : null,
    //   italic: true,
    // },
    {
      label: 'Variety',
      name: order.productVariety?.varietyName,
      img: order.productVariety?.img,
      hsCode: null,
      badge: order.productVariety?.grade,
      moq: order.productVariety?.slabAdjustedMOQ
        ? `Min. MOQ: ${order.productVariety.slabAdjustedMOQ} ${order.productVariety.standardizedUNIT || ''}`
        : null,
      isLast: true,
    },
  ];

  return (
    <div className="w-full">
      {/* Desktop: horizontal flow */}
      {/* <div className="hidden sm:flex items-stretch gap-0">
        {steps.map((step, i) => (
          <React.Fragment key={step.label}>
            <div className={`flex-1 flex flex-col rounded-2xl overflow-hidden border transition-all
              ${step.isLast
                ? 'border-[#772037]/30 bg-gradient-to-b from-[#772037]/5 to-[#CC234F]/5 shadow-md shadow-[#772037]/10'
                : 'border-gray-100 bg-white'
              }`}>
              <div className={`relative w-full aspect-video overflow-hidden
                ${step.isLast ? 'bg-[#772037]/5' : 'bg-gray-50'}`}>
                {step.img ? (
                  <img
                    src={step.img}
                    alt={step.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-3xl text-gray-200">
                    📦
                  </div>
                )}
                <div className={`absolute top-2 left-2 text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded-full
                  ${step.isLast ? 'bg-[#772037] text-white' : 'bg-black/50 text-white backdrop-blur-sm'}`}>
                  {step.label}
                </div>
                {step.isLast && (
                  <div className="absolute inset-0 ring-2 ring-inset ring-[#772037]/20 rounded-none pointer-events-none" />
                )}
              </div>

              <div className="p-2.5 flex flex-col gap-1 flex-1">
                <p className={`text-xs font-bold leading-tight line-clamp-2 ${step.isLast ? 'text-[#772037]' : 'text-gray-800'}`}>
                  {step.name || '—'}
                </p>
                {step.badge && (
                  <span className={`self-start text-[9px] font-bold px-1.5 py-0.5 rounded
                    ${step.isLast ? 'bg-[#772037]/10 text-[#772037]' : 'bg-gray-100 text-gray-500'} uppercase tracking-wide`}>
                    {step.badge}
                  </span>
                )}
                {step.moq && (
                  <p className="text-[10px] text-gray-400 font-medium">{step.moq}</p>
                )}
                {step.hsCode && (
                  <p className="text-[9px] text-gray-400 font-mono">HS: {step.hsCode}</p>
                )}
              </div>
            </div>

            {i < steps.length - 1 && (
              <div className="flex items-center px-1 flex-shrink-0">
                <svg className="w-4 h-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            )}
          </React.Fragment>
        ))}
      </div> */}

      {/* Mobile: vertical list */}
      <div className="space-y-2">
        {steps.map((step, i) => (
          <div key={step.label}
            className={`flex items-center gap-3 rounded-xl border p-2.5
              ${step.isLast
                ? 'border-[#772037]/25 bg-gradient-to-r from-[#772037]/5 to-transparent'
                : 'border-gray-100 bg-white'}`}>
            {/* Connector line left of non-first items */}
            <div className="flex flex-col items-center self-stretch mr-1">
              {i > 0 && <div className="w-px flex-1 bg-gray-200 mb-1" />}
              <div className={`w-2 h-2 rounded-full flex-shrink-0 ${step.isLast ? 'bg-[#772037]' : 'bg-gray-300'}`} />
              {i < steps.length - 1 && <div className="w-px flex-1 bg-gray-200 mt-1" />}
            </div>

            {/* Image */}
            <div className={`w-12 h-12 rounded-xl overflow-hidden border flex-shrink-0
              ${step.isLast ? 'border-[#772037]/20' : 'border-gray-100'}`}>
              {step.img ? (
                <img src={step.img} alt={step.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-50 text-xl">📦</div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded
                  ${step.isLast ? 'bg-[#772037] text-white' : 'bg-gray-100 text-gray-500'}`}>
                  {step.label}
                </span>
                {step.badge && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase">
                    {step.badge}
                  </span>
                )}
              </div>
              <p className={`text-sm font-bold mt-0.5 truncate ${step.isLast ? 'text-[#772037]' : 'text-gray-800'}`}>
                {step.name || '—'}
              </p>
              {step.moq && <p className="text-[10px] text-gray-400 mt-0.5">{step.moq}</p>}
              {step.hsCode && <p className="text-[9px] text-gray-400 font-mono mt-0.5">HS: {step.hsCode}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Reusable UI Atoms ────────────────────────────────── */
const SectionCard = ({ title, icon, children, className = '' }) => (
  <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden ${className}`}>
    {title && (
      <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50/70">
        {icon && <span className="text-sm">{icon}</span>}
        <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">{title}</h3>
      </div>
    )}
    <div className="px-4 sm:px-5 py-4">{children}</div>
  </div>
);

const InfoRow = ({ label, value, valueClass = '' }) => (
  <div className="flex flex-col xs:flex-row xs:items-start xs:justify-between gap-0.5 py-2.5 border-b border-gray-100 last:border-0">
    <span className="text-xs font-medium text-gray-400 uppercase tracking-wide flex-shrink-0 min-w-[130px]">{label}</span>
    <span className={`text-sm font-semibold text-right break-words ${valueClass || 'text-gray-800'}`}>{value ?? '—'}</span>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    approved: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
    rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', dot: 'bg-red-500' },
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-400' },
  };
  const key = status?.toLowerCase() || 'pending';
  const s = map[key] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border ${s.bg} ${s.text} ${s.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot} ${key === 'pending' ? 'animate-pulse' : ''}`} />
      {status === "Approved" ? "Approved by QQT" : status === "Rejected" ? "Rejected by QQT" : "Pending Approval"}
    </span>
  );
};

const Chip = ({ emoji, value, colorClass = 'bg-white/15 text-white' }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${colorClass}`}>
    {emoji && <span>{emoji}</span>}
    {value}
  </span>
);

const dispatchStatusColor = (s) => {
  switch (s?.toLowerCase()) {
    case 'dispatched': return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'delivered': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
    default: return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};

const quotationMatchColor = (s) => {
  switch (s) {
    case 'Match_Complete': return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
    case 'Match_In_Progress': return 'bg-blue-50 text-blue-700 border border-blue-200';
    case 'No_Match': return 'bg-red-50 text-red-700 border border-red-200';
    default: return 'bg-gray-50 text-gray-600 border border-gray-200';
  }
};

/* ─── Main Component ───────────────────────────────────── */
const OrderDetailCard = ({ order, onShowTransactions }) => {
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  if (!order) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] text-center p-8">
        <div className="w-16 h-16 rounded-2xl bg-[#772037]/8 flex items-center justify-center mb-4">
          <span className="text-3xl">📋</span>
        </div>
        <p className="text-gray-400 font-medium text-sm">Select an order to view details</p>
      </div>
    );
  }

  const baseStages = [
    { key: 'Order', label: 'Order Placed', icon: '📋' },
    { key: 'Quotation', label: 'Quotation', icon: '💰' },
    { key: 'Sampling', label: 'Sampling', icon: '🧪' },
    { key: 'Order-Fullfillment', label: 'Order Fulfillment', icon: '📦' },
    { key: 'Delivery_And_Payment', label: 'Delivery & Payment', icon: '🚚' },
    { key: 'Completed', label: 'Completed', icon: '✅' },
  ];
  const stages = order.stages === 'Closed'
    ? [{ key: 'Order', label: 'Order Placed', icon: '📋' }, { key: 'Closed', label: 'Closed', icon: '❌' }]
    : baseStages;
  const currentStageIndex = stages.findIndex((s) => s.key === order.stages);

  const allowedStages = ["Quotation",
    "Sampling",
    "Order-Fullfillment",
    "Delivery_And_Payment",
    "Dispute_Raised",
    "Dispute_Under_Investigation",
    "Full_Order_Returned",
    "Partial_Order_Returned",
    "Closed",
    "Completed"];
  const showQuotationBtn = allowedStages.some((s) => order.stages?.includes(s)) && order.review?.status === 'Approved';

  const allMedia = [
    ...(order.referencePhotoAndVideo?.images || []),
    ...(order.referencePhotoAndVideo?.videos || []),
  ];
  const isVideo = (url) => url?.includes('.mp4') || url?.includes('.webm') || url?.includes('video');

  const fmtDate = (d) => d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';
  const fmtDateTime = (d) => d
    ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';

  return (
    <div className="h-full overflow-y-auto space-y-4 pb-6 pr-0.5">

      {/* ══ 1. HEADER BANNER ══════════════════════════════ */}
      <div className="bg-gradient-to-br shineEffect from-[#772037] to-[#CC234F] rounded-2xl p-4 sm:p-5 text-white shadow-lg shadow-[#772037]/20">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <p className="text-[10px] font-bold text-white/50 uppercase tracking-widest mb-1">Order Reference</p>
            <h2 className="text-lg sm:text-xl font-bold tracking-tight">{order.orderNumber || `#${order._id?.slice(-6)}`}</h2>
            <p className="text-xs text-white/60 mt-0.5">Placed on {fmtDate(order.createdAt)}</p>
          </div>
          <div className="flex flex-row sm:flex-col items-center sm:items-end gap-2 flex-wrap">
            <StatusBadge status={order.review?.status} />
            {showQuotationBtn && (
              <button
                title='Click to view quotations related to this order'
                onClick={() => navigate(`/quotations?highlight=${order._id}`)}
                className="text-xs px-3 py-2 rounded-xl bg-white text-[#772037] hover:bg-white/90 transition font-bold shadow-sm whitespace-nowrap"
              >
                Click to view quotations →
              </button>
            )}
            <button
              title='Click to view payment transactions'
              onClick={() => onShowTransactions && onShowTransactions()}
              className="text-xs px-3 py-2 rounded-xl bg-white text-[#772037] hover:bg-white/90 transition font-bold shadow-sm whitespace-nowrap border border-white/40"
            >
              📜 Click to view transactions
            </button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {order.stages && (
            <Chip
              emoji="📍"
              value={order.stages.replace(/_/g, ' ')}
              colorClass={order.stages === 'Closed' ? 'bg-red-900/40 text-red-200' : 'bg-white/15 text-white'}
            />
          )}
        </div>
      </div>

      {/* ══ DISPUTE ALERT BANNER ══ */}
      {isDisputeOrder(order.stages) && (
        <DisputeAlertBanner currentStage={order.stages} />
      )}

      {/* ══ 2. ORDER PROGRESS ═════════════════════════════ */}
      {/* ─ Show dispute flow instead of normal flow if in dispute ─ */}
      {isDisputeOrder(order.stages) ? (
        <>
          <SectionCard title="Dispute Resolution Timeline" icon="⚖️">
            <DisputeStageTimeline currentStage={order.stages} />
            <div className="mt-5 flex justify-center">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-amber-50 text-amber-600 border border-amber-200">
                <span className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
                Current Status: {DISPUTE_STAGES.find(s => s.key === order.stages)?.label || order.stages?.replace(/_/g, ' ')}
              </span>
            </div>
          </SectionCard>

          {/* ══ RETURN DISPATCH (Partial or Full Return) ══ */}
          {(order.stages === "Partial_Order_Returned" || order.stages === "Full_Order_Returned") && (
            <ReturnDispatchCard
              orderId={order._id}
              orderNumber={order.orderNumber}
              stage={order.stages}
            />
          )}
        </>
      ) : (
        <SectionCard title="Order Progress" icon="🗺️">
          <StageTimeline stages={stages} currentStageIndex={currentStageIndex} orderStages={order.stages} />
          <div className="mt-5 flex justify-center">
            {order.stages === 'Closed' ? (
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-red-50 text-red-600 border border-red-200">
                <span className="w-2 h-2 rounded-full bg-red-500" /> Order Closed
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold bg-[#772037]/6 text-[#772037] border border-[#772037]/20">
                <span className="w-2 h-2 rounded-full bg-[#772037] animate-ping" />
                Currently: {stages[currentStageIndex]?.label || order.stages?.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        </SectionCard>
      )}

      {/* ══ 3. PRODUCT HIERARCHY (Category → SubCat → Product → Variety) ══ */}
      <SectionCard title="Product Variety You Have Ordered" icon="🏷️">
        <ProductHierarchy order={order} />
      </SectionCard>

      {/* ══ 4. PRICE & QUANTITY ═══════════════════════════ */}
      {order.priceAndQuantity && (
        <SectionCard title="Price & Quantity" icon="💰">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="col-span-2 bg-gradient-to-br from-[#772037]/6 to-[#CC234F]/6 border border-[#772037]/15 rounded-xl p-4 text-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">
                {order.priceAndQuantity.expectedPriceRange?.message || 'Expected Price'}
              </p>
              <p className="text-xl font-extrabold text-[#772037]">
                {order.priceAndQuantity.currency}{" "}
                {order.priceAndQuantity?.expectedPriceRange?.min === 0 &&
                  order.priceAndQuantity?.expectedPriceRange?.max === 0
                  ? order.priceAndQuantity?.fixedPrice?.price?.toLocaleString()
                  : (
                    <>
                      {order.priceAndQuantity?.expectedPriceRange?.min?.toLocaleString()}
                      <span className="text-gray-400 font-normal mx-1">–</span>
                      {order.priceAndQuantity?.expectedPriceRange?.max?.toLocaleString()}
                    </>
                  )}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">per {order.priceAndQuantity.standardizedUnit}</p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5 text-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Order Requirement</p>
              <p className="text-xl font-extrabold text-gray-800">{order.priceAndQuantity.MOQ?.toLocaleString()}</p>
              <p className="text-xs text-gray-400">{order.priceAndQuantity.standardizedUnit}</p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5 text-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Currency</p>
              <p className="text-xl font-extrabold text-gray-800">{order.priceAndQuantity.currency}</p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ══ 5. DELIVERY WINDOW ════════════════════════════ */}
      {order.expectedDeliveryTime && (
        <SectionCard title="Expected Delivery Window" icon="📅">
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
            <div className="flex-1 w-full bg-gray-50 border border-gray-100 rounded-xl p-3.5 text-center">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">From</p>
              <p className="text-base font-bold text-gray-800">{fmtDate(order.expectedDeliveryTime.fromDate)}</p>
            </div>
            <div className="text-[#772037]">
              <svg className="w-5 h-5 hidden sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
              <svg className="w-5 h-5 sm:hidden rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>
            <div className="flex-1 w-full bg-[#772037]/5 border border-[#772037]/15 rounded-xl p-3.5 text-center">
              <p className="text-[10px] font-bold text-[#772037]/50 uppercase tracking-widest mb-1">To</p>
              <p className="text-base font-bold text-[#772037]">{fmtDate(order.expectedDeliveryTime.toDate)}({getDeliveryDays(order.expectedDeliveryTime.fromDate, order.expectedDeliveryTime.toDate)} days)</p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ══ 6. SPECIFICATIONS ════════════════════════════ */}
      {(order.specification?.relatedQuestionsAndAnswers?.length > 0 || order.specification?.otherRequirements?.length > 0) && (
        <SectionCard title="Specifications & Requirements" icon="📐">
          {order.specification?.relatedQuestionsAndAnswers?.length > 0 && (
            <div className="mb-4">
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Questions and Answers related to the Product</p>
              <div className="space-y-2.5">
                {order.specification.relatedQuestionsAndAnswers.map((qa, i) => (
                  <div key={qa._id || i} className="bg-gray-50 border border-gray-100 rounded-xl p-3.5">
                    <p className="text-xs font-semibold text-gray-500 mb-1.5 leading-snug">Q: {qa.question}</p>
                    <p className="text-sm font-bold text-gray-800">A: {qa.answeredByBuyer || '—'}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {order.specification?.otherRequirements?.length > 0 && (
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-3">Other Requirements</p>
              <ul className="space-y-2">
                {order.specification.otherRequirements.map((req, i) => (
                  <li key={req._id || i} className="flex items-start gap-2.5">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#772037] flex-shrink-0" />
                    <span className="text-sm text-gray-700 font-medium">{req.buyersRequirement}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </SectionCard>
      )}

      {/* ══ 7. REFERENCE MEDIA ═══════════════════════════ */}
      {allMedia.length > 0 && (
        <SectionCard title="Reference Media" icon="🖼️">
          <div className="flex flex-wrap gap-3">
            {allMedia.map((url, i) => (
              <button
                key={i}
                onClick={() => { setModalIndex(i); setModalOpen(true); }}
                className="group relative w-20 h-20 md:w-32 md:h-32 rounded-xl overflow-hidden border border-gray-200 hover:border-[#772037]/40 transition-all hover:shadow-md hover:scale-[1.02] bg-gray-100"
              >
                {isVideo(url) ? (
                  <div className="w-full h-full flex items-center justify-center bg-gray-900">
                    <span className="text-3xl">▶️</span>
                  </div>
                ) : (
                  <img src={url} alt={`Media ${i + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors rounded-xl" />
                <div className="absolute bottom-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="bg-black/70 text-white text-[10px] px-1.5 py-0.5 rounded-md font-medium">
                    {isVideo(url) ? 'Play' : 'View'}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ══ 8. DELIVERY ADDRESS ══════════════════════════ */}
      {order.address && (
        <SectionCard title="Delivery Address" icon="📍">
          <div className="flex gap-3 items-start">
            <div className="w-10 h-10 rounded-xl bg-[#772037]/8 flex items-center justify-center flex-shrink-0">
              <span className="text-lg">🏢</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="text-sm font-bold text-gray-800">{order.address.addressType || 'Address'}</span>
                {order.address.isDefault && (
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#772037]/10 text-[#772037]">Default</span>
                )}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                {[order.address.addressLine1, order.address.addressLine2].filter(Boolean).join(', ')}
              </p>
              <p className="text-sm text-gray-500 mt-0.5">
                {[order.address.city, order.address.state, order.address.country, order.address.pincode].filter(Boolean).join(', ')}
              </p>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ══ 10. DISPATCH DETAILS ════════════════════════ */}
      {order.dispatchDetails?.length > 0 && (
        <SectionCard title="Dispatch Details" icon="🚛">
          <div className="space-y-3">
            {order.dispatchDetails.map((d, i) => (
              <div key={d._id || i} className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-bold text-gray-700">Dispatch #{i + 1}</span>
                    {d.type && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white border border-gray-200 text-gray-500">
                        {d.type}
                      </span>
                    )}
                  </div>
                  {d.status && (
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${dispatchStatusColor(d.status)}`}>
                      {d.status}
                    </span>
                  )}
                </div>
                <div className="px-4 py-1 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                  <InfoRow label="Dispatched At" value={d.dispatchedAt ? fmtDateTime(d.dispatchedAt) : null} />
                  <InfoRow label="Delivered At" value={d.deliveredAt ? fmtDateTime(d.deliveredAt) : 'Pending'} />
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ══ 11. REVIEW DETAILS ══════════════════════════ */}
      {(order.review?.reviewNote || order.review?.reviewedAt || order.review?.reviewedBy) && (
        <SectionCard title="Review Details" icon="✅">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 mb-3">
            {order.review?.reviewedBy?.email && (
              <InfoRow label="Reviewed By" value={order.review.reviewedBy.email} />
            )}
            <InfoRow label="Reviewed At" value={fmtDateTime(order.review.reviewedAt)} />
            <InfoRow
              label="Status"
              value={order.review.status}
              valueClass={
                order.review.status === 'Approved' ? 'text-emerald-600' :
                  order.review.status === 'Rejected' ? 'text-red-600' : 'text-amber-600'
              }
            />
          </div>
          {order.review.reviewNote && (
            <div className={`rounded-xl p-3.5 text-sm leading-relaxed border
              ${order.review.status === 'Approved' ? 'bg-emerald-50 text-emerald-800 border-emerald-200' :
                order.review.status === 'Rejected' ? 'bg-red-50 text-red-800 border-red-200' :
                  'bg-amber-50 text-amber-800 border-amber-200'}`}>
              <p className="text-[10px] font-bold uppercase tracking-widest opacity-50 mb-1">Review Note</p>
              {order.review.reviewNote}
            </div>
          )}
        </SectionCard>
      )}

      {modalOpen && (
        <ImageModal
          images={order.referencePhotoAndVideo?.images || []}
          videos={order.referencePhotoAndVideo?.videos || []}
          initialIndex={modalIndex}
          onClose={() => setModalOpen(false)}
        />
      )}

    </div>
  );
};

export default OrderDetailCard;